import network from 'utils/network';

import { clientCookies } from 'utils/cookies';

import {
  DYNAMIC_YIELD_BETA_FEATURE,
  DY_JUST_LOGGED_IN_DATA_URL,
  DY_COOKIE_NAME,
  DY_SESSION_COOKIE_NAME,
  DY_RETRY_TIMEOUT,
  PageContextType,
} from './consts';

export const getProductPrice = (price, quantity) =>
  Math.round((quantity * price) * 1000) / 1000;

export const prepareCategoryNames = (categories = []) =>
  categories.map(category => category.name);

export const updateProductInCart = (cart, product, quantityDelta) => {
  if (product && product.sku) {
    const productIndex = cart.findIndex(item => item.productId === product.sku);
    const isInBasket = productIndex !== -1;
    const productQuantity = isInBasket ? quantityDelta + cart[productIndex].quantity : Math.abs(quantityDelta);

    if (isInBasket) {
      cart.splice(productIndex, 1);
    }

    if (productQuantity > 0) {
      cart.push({
        productId: product.sku,
        quantity: productQuantity,
        itemPrice: product.price.current,
      });
    }
  }

  return cart;
};

export const isDynamicYieldEnabled = (betaFeatures) =>
  !!(betaFeatures && betaFeatures.includes(DYNAMIC_YIELD_BETA_FEATURE));

export const getContextDataForCatalogue = (categories = []) => {
  const type = categories && categories.length > 0 ? PageContextType.CATEGORY : PageContextType.OTHER;
  const context = { type };

  if (type !== PageContextType.OTHER) {
    context.data = prepareCategoryNames(categories);
  }

  return context;
};

export const getProductPropertiesForEvent = (product, quantity) => ({
  productId: product.sku,
  value: getProductPrice(product.price.current, quantity),
  quantity,
});

export const getCartForEvent = (basketItems, product = {}, quantityDelta = 0) => {
  const cart = [];
  basketItems.forEach(item => {
    cart.push({
      productId: item.sku,
      quantity: item.quantity,
      itemPrice: item.pricePerUnit || item.price,
    });
  });

  return updateProductInCart(cart, product, quantityDelta);
};

export const calculateOrderDifference = (previousOrder, newOrder) => {
  // Get list of skus with quantities for the order
  const getSkusWithQuantity = (order) => {
    const skus = {};
    if (order.products && order.products.length > 0) {
      for (const product of order.products) {
        skus[product.sku] = product.quantity;
      }
    }

    return skus;
  };

  // Get order difference if any
  // order - one to compare with, depending on direction
  // skusMap - { sku -> quantity } pairs to compare with
  const getOrderDiff = (order, skusMap) => {
    if (order.products && order.products.length > 0) {
      const productsDiff = [];

      for (const product of order.products) {
        const skuExists = Object.keys(skusMap).includes(product.sku);
        if (skuExists) {
          const quantityDiff = product.quantity - skusMap[product.sku];

          // Product quantity has changed between orders
          // and is aligned with the difference that is calculated:
          // - Addition: new order quantity should be greater than the previous one
          // - Removal: previous order quantity should be greater than the new one
          if (quantityDiff > 0) {
            productsDiff.push({
              ...product,
              quantity: Math.abs(quantityDiff),
            });
          }
        } else {
          productsDiff.push(product);
        }
      }

      // If there is actually a difference in orders..
      if (productsDiff.length > 0) {
        return {
          value: productsDiff.reduce((total, product) => total + getProductPrice(product.price, product.quantity), 0),
          cart: productsDiff.map(product => ({
            productId: product.sku,
            quantity: product.quantity,
            itemPrice: getProductPrice(product.price, 1),
          })),
        };
      }
    }

    return null;
  };

  return {
    addedDiff: getOrderDiff(newOrder, getSkusWithQuantity(previousOrder)),
    removedDiff: getOrderDiff(previousOrder, getSkusWithQuantity(newOrder)),
  };
};

export const getUserDataFromCookies = async (isUserOptOut) => {
  if (isUserOptOut) {
    return Promise.resolve({
      user: {
        active_consent_accepted: false,
      },
    });
  }

  return new Promise(function getData(resolve, reject, shouldRetry = true) {
    const dyid = clientCookies.get(DY_COOKIE_NAME) || '';
    const dysession = clientCookies.get(DY_SESSION_COOKIE_NAME) || '';

    if (dyid && dysession) {
      resolve({
        user: {
          dyid,
          dyid_server: dyid,
          active_consent_accepted: true,
        },
        session: {
          dy: dysession,
        },
      });
      return;
    }

    // If cookie data does not exist yet, retry.
    if (shouldRetry) {
      setTimeout(
        () => { getData(resolve, reject, false); }, DY_RETRY_TIMEOUT
      );
    } else {
      reject();
    }
  });
};

export const getUserJustLoggedIn = () =>
  !!network && network.get(`${DY_JUST_LOGGED_IN_DATA_URL}`).catch(
    () => false
  );
